import { useCallback } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import { sanitizeEmailHtml } from '../lib/sanitizeHtml';

interface ComposeEditorProps {
  html: string;
  onChange: (html: string) => void;
  /** Merge fields offered in the insert menu, from the uploaded CSV. */
  mergeFields: string[];
  /**
   * Whether to show the "Insert merge field" affordance. Off by default while
   * mail-merge is hidden from the UI; the substitution engine still runs on send.
   */
  showMergeFields?: boolean;
}

function MarkButtons({ editor }: { editor: Editor }) {
  const marks = [
    { name: 'bold', label: 'Bold', text: 'B', style: { fontWeight: 700 } },
    { name: 'italic', label: 'Italic', text: 'I', style: { fontStyle: 'italic' } },
    { name: 'underline', label: 'Underline', text: 'U', style: { textDecoration: 'underline' } },
  ] as const;

  return (
    <ToggleButtonGroup size="small" aria-label="Text formatting">
      {marks.map((mark) => (
        <ToggleButton
          key={mark.name}
          value={mark.name}
          selected={editor.isActive(mark.name)}
          aria-label={mark.label}
          aria-pressed={editor.isActive(mark.name)}
          onClick={() => editor.chain().focus().toggleMark(mark.name).run()}
          sx={{ minWidth: 44, minHeight: 44, ...mark.style }}
        >
          {mark.text}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

export default function ComposeEditor({
  html,
  onChange,
  mergeFields,
  showMergeFields = false,
}: ComposeEditorProps) {
  const [mergeAnchor, setMergeAnchor] = useState<HTMLElement | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({
        placeholder: 'Paste your email here from Gmail, or start typing…',
      }),
    ],
    content: html,
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': 'Email body',
      },
      /**
       * Gmail pastes carry font tags, inline font-family on every node, and
       * quote wrappers. Cleaning here — before ProseMirror parses — means the
       * document is never polluted in the first place.
       */
      transformPastedHTML: (pasted) => sanitizeEmailHtml(pasted),
    },
    onUpdate: ({ editor: current }) => {
      onChange(current.getHTML());
    },
  });

  const insertMergeField = useCallback(
    (field: string) => {
      editor?.chain().focus().insertContent(`{{${field}}}`).run();
      setMergeAnchor(null);
    },
    [editor],
  );

  if (!editor) return null;

  return (
    <Paper variant="outlined">
      <Stack
        direction="row"
        spacing={1}
        sx={{ p: 1, flexWrap: 'wrap', alignItems: 'center' }}
        role="toolbar"
        aria-label="Formatting"
      >
        <MarkButtons editor={editor} />

        <ToggleButtonGroup size="small" aria-label="Blocks">
          <ToggleButton
            value="bulletList"
            selected={editor.isActive('bulletList')}
            aria-label="Bulleted list"
            aria-pressed={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            List
          </ToggleButton>
          <ToggleButton
            value="blockquote"
            selected={editor.isActive('blockquote')}
            aria-label="Block quote"
            aria-pressed={editor.isActive('blockquote')}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            Quote
          </ToggleButton>
        </ToggleButtonGroup>

        {showMergeFields && (
          <>
            <Tooltip title="Insert a value from your CSV, e.g. the guest's first name">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={mergeFields.length === 0}
                  aria-haspopup="menu"
                  onClick={(event) => setMergeAnchor(event.currentTarget)}
                >
                  Insert merge field
                </Button>
              </span>
            </Tooltip>
            <Menu
              anchorEl={mergeAnchor}
              open={Boolean(mergeAnchor)}
              onClose={() => setMergeAnchor(null)}
            >
              {mergeFields.map((field) => (
                <MenuItem key={field} onClick={() => insertMergeField(field)} sx={{ minHeight: 44 }}>
                  {`{{${field}}}`}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Stack>

      <Divider />

      <Box className="email-editor">
        <EditorContent editor={editor} />
      </Box>
    </Paper>
  );
}
